import React, { useState, useRef, useEffect } from "react";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import instance from "../../services/services";
import { INQUIRY_REGISTER_URL, INQUIRY_DETAIL_URL, INQUIRY_EDIT_URL } from "../../utils/urls";
import PopupLoginError from "../../components/Popup/LoginError";
import { useLanguage } from "../../hooks/useLanguage";

function CustomizingForm(){
  const { t } = useLanguage();
  const { myInfo } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const fileInputRef = useRef(null);
  
  // URL 쿼리 파라미터에서 b_seq 추출
  const queryParams = new URLSearchParams(location.search);
  const b_seq = queryParams.get('b_seq');
  const isEdit = !!b_seq; // b_seq가 존재하면 수정 모드
  
  // 폼 상태 관리
  const [formData, setFormData] = useState({
    subject: '',
    contents: ''
  });
  
  // 이미지 상태 관리
  const [uploadImages, setUploadImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [deletedFileIds, setDeletedFileIds] = useState([]);
  
  // CustomizingForm에서 location.state 확인
  const { from } = location.state || {};
  const isFromMyPage = from === 'mypage';
  
  // 수정 모드일 경우 기존 데이터 불러오기
  useEffect(() => {
    if (isEdit) {
      const fetchDetailData = async () => {
        try {
          const response = await instance.post(INQUIRY_DETAIL_URL, {
            b_seq: b_seq
          });
          
          const detailData = response.data.data.data;
          const files = response.data.data.files || [];
          
          // 폼 데이터 설정
          setFormData({
            subject: detailData.subject || '',
            contents: detailData.contents || ''
          });
          
          // 기존 이미지 미리보기 설정
          const filesPreviews = files.map(file => ({
            isExisting: true,
            file_seq: file.file_seq,
            url: file.new_filepath
          }));
          
          setPreviewImages(filesPreviews);
          
        } catch (error) {
          console.error("커스터마이징 상세 데이터 가져오기 실패:", error);
          setIsPopupOpen("데이터를 불러오는데 실패했습니다.");
        }
      };
      
      fetchDetailData();
    }
  }, [isEdit, b_seq]);
  
  // 입력 필드 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // 이미지 파일 선택 핸들러
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // 최대 5개까지만 허용 (기존 이미지 + 새 이미지)
    const remainingSlots = 5 - previewImages.length;
    const selectedFiles = files.slice(0, remainingSlots);
    
    if (selectedFiles.length === 0) return;
    
    // 업로드할 파일 리스트 업데이트
    setUploadImages(prev => [...prev, ...selectedFiles]);
    
    // 미리보기 이미지 URL 생성
    const newPreviewImages = selectedFiles.map(file => ({
      isExisting: false, // 새 파일임을 표시
      file,
      url: URL.createObjectURL(file)
    }));
    
    setPreviewImages(prev => [...prev, ...newPreviewImages]);
    
    // 파일 선택 후 input 값 초기화
    e.target.value = null;
  };
  
  // 이미지 삭제 핸들러
  const handleImageDelete = (index) => {
    const imageToDelete = previewImages[index];
    
    if (imageToDelete.isExisting) {
      // 기존 이미지인 경우 삭제 목록에 추가
      setDeletedFileIds(prev => [...prev, imageToDelete.file_seq]);
    } else {
      // 새로 추가한 이미지인 경우 URL 해제
      URL.revokeObjectURL(imageToDelete.url);
      
      // 새 이미지 중에서 해당 이미지의 인덱스 찾기
      const newImagesCount = previewImages.filter(img => !img.isExisting).length;
      const newImageIndex = index - (previewImages.length - newImagesCount);
      
      if (newImageIndex >= 0) {
        setUploadImages(prev => prev.filter((_, i) => i !== newImageIndex));
      }
    }
    
    // 미리보기에서 제거
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
  };
  
  // 취소 버튼 핸들러
  const handleCancel = () => {
    navigate('/community/customizing');
  };
  
  // 등록/수정 버튼 핸들러
  const handleSubmit = async () => {
    // 필수 필드 검증
    if (!formData.subject) {
      setIsPopupOpen(t("title_input"));
      return;
    }
    
    if (!formData.contents) {
      setIsPopupOpen(t("content_input_2_0617"));
      return;
    }
    
    try {
      const submitFormData = new FormData();
      
      // 기본 데이터 추가
      submitFormData.append('boardid', 'custom');
      submitFormData.append('subject', formData.subject);
      submitFormData.append('contents', formData.contents);
      
      // 수정 모드일 경우 b_seq 추가
      if (isEdit) {
        submitFormData.append('b_seq', b_seq);
        
        // 삭제할 파일 ID 추가
        if (deletedFileIds.length > 0) {
          deletedFileIds.forEach(id => {
            submitFormData.append('delete_file_id[]', id);
          });
        }
      }
      
      // 이미지 파일 추가
      uploadImages.forEach(file => {
        submitFormData.append('upload_images[]', file);
      });
      
      // API URL 결정
      const apiUrl = isEdit ? INQUIRY_EDIT_URL : INQUIRY_REGISTER_URL;
      
      const response = await instance.post(apiUrl, submitFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setIsPopupOpen({
          message: isEdit ? t('inquiry_modified2_txt') : t('inquiry_registered2'),
          action: 'navigate_list'
        });
      } else {
        setIsPopupOpen(isEdit ? t('inquiry_failed3') : t('inquiry_failed4'));
      }
    } catch (error) {
      console.error(isEdit ? '수정 실패:' : '등록 실패:', error);
      setIsPopupOpen(isEdit ? '수정 중 오류가 발생했습니다.' : '등록 중 오류가 발생했습니다.');
    }
  };
  
  // 팝업 확인 버튼 핸들러
  const handleConfirm = () => {
    if (isPopupOpen.action === 'navigate_list') {
      // 마이페이지에서 온 경우 마이페이지로 이동, 아니면 커뮤니티로 이동
      const targetPath = isFromMyPage ? '/myPage/customizing' : '/community/customizing';
      navigate(targetPath);
    }
    setIsPopupOpen(false);
  };

  return(
    <div className="board_container order_inner">
      <h3 className="commu_title ">{isEdit ? t('inquiry_modified3') : t('customization')}</h3>
      <ul className="default_info_table board_list inquiry_form">
        <li className="info_item user_id">
          <div className="label">
            <label htmlFor="userid">{t("id")}</label>
          </div>
          <div className="form_info">
            <div className="info_detail default_input  w-half">
              <input
                type="text"
                className="readonly"
                id="userid"
                value={myInfo?.userid}
                readOnly
              />
            </div>
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="subject">{t("title")}</label>
          </div>
          <div className="form_info">
            <div className="info_detail default_input">
              <input
                type="text"
                name="subject"
                id="subject"
                placeholder={t("title_input")}
                value={formData.subject}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <label htmlFor="contents">{t("content")}</label>
          </div>
          <div className="form_info">
            <div className="info_detail default_input">
              <textarea
                name="contents"
                id="contents"
                placeholder={t("content_input")}
                value={formData.contents}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </li>

        <li className="info_item">
          <div className="label">
            <span>{t("image")}</span>
          </div>
          <div className="form_info flex-wrap">
            {/* 이미지 첨부 */}
            <div className="info_detail">
              <div className="attach_file">
                <div className="img_attach_box">
                  {previewImages.length < 5 && (
                    <label
                      htmlFor="sub_img_attach"
                      className="default_btn ic_lg white attach_btn"
                    />
                  )}
                  <input
                    type="file"
                    className="i_hide"
                    id="sub_img_attach"
                    accept=".png,.jpg"
                    multiple
                    name="img_file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />
                  <ul className="preview_list">
                    {previewImages.map((image, index) => (
                      <li key={index} className="default_btn ic_lg">
                        <img
                          className="object-cover"
                          src={image.url}
                          alt={`이미지 첨부 ${index + 1}`}
                          loading="lazy"
                        />
                        <button
                          type="button"
                          className="delete_btn"
                          onClick={() => handleImageDelete(index)}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="dash_list flex-2 column w-100 commu_img">
                  <span>
                    {t("file_capacity_limit")}
                  </span>
                  <span>{t("maximum_5_images")}</span>
                </p>
              </div>
            </div>
          </div>
        </li>
      </ul>

      {/* 목록 버튼 */}
      <div className="submit_box bg mt_32">
        <button
          className="default_btn white lg"
          onClick={handleCancel}
        >{t("cancel")}
        </button>
        <button
          className="default_btn black lg"
          onClick={handleSubmit}
        >{isEdit ? t("modify") : t("register")}
        </button>
      </div>
      
      <PopupLoginError isPopup={isPopupOpen} setIsPopup={setIsPopupOpen} onConfirm={handleConfirm}/>
    </div>
  )
};

export default CustomizingForm;